import axios from 'axios';
import { shouldRedirectToLoginOn401 } from './publicSpaPaths';
import { createSessionRefreshCoordinator } from './sessionRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

const PRIVILEGED_SESSION_KEY = 'tagdada_privileged_session';
export const PRIVILEGED_SESSION_EVENT = 'tagdada:privileged-session';

const PRIVILEGED_DENY_DETAILS = new Set([
  'mfa_required',
  'privileged_session_revoked',
  'step_up_required',
]);

function privilegedDenyDetail(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    return detail.error || detail.message || '';
  }
  return '';
}

export function setPrivilegedSession(token) {
  if (typeof window === 'undefined') return;
  if (token) window.sessionStorage.setItem(PRIVILEGED_SESSION_KEY, token);
  else window.sessionStorage.removeItem(PRIVILEGED_SESSION_KEY);
  try {
    window.dispatchEvent(new CustomEvent(PRIVILEGED_SESSION_EVENT, {
      detail: { active: Boolean(token) },
    }));
  } catch { /* ignore */ }
}

export function hasPrivilegedSession() {
  return typeof window !== 'undefined' && Boolean(window.sessionStorage.getItem(PRIVILEGED_SESSION_KEY));
}

/** Clear browser-held privileged MFA token (ISSUE-0016). */
export function clearPrivilegedSessionClient() {
  setPrivilegedSession('');
}

function csrfToken() {
  if (typeof document === 'undefined') return '';
  return document.cookie.split('; ').find((part) => part.startsWith('tagdada_csrf='))?.split('=').slice(1).join('=') || '';
}

// Coerce an axios error into a safe, renderable string. The credit gate
// returns 402 with an OBJECT detail ({error, needed, balance}); passing that
// straight to toast.error / JSX crashes React ("Objects are not valid as a
// React child"), so always route API errors through this helper.
export function errText(e, fallback = '', insufficientMsg = '') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string' && d) return d;
  if (d && typeof d === 'object') {
    if (d.error === 'insufficient_credits') return insufficientMsg || fallback;
    return d.error || d.message || fallback;
  }
  return fallback;
}

api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options', 'trace'].includes(method)) {
    const csrf = csrfToken();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  if (typeof window !== 'undefined') {
    const privilegedSession = window.sessionStorage.getItem(PRIVILEGED_SESSION_KEY);
    if (privilegedSession) config.headers['X-Privileged-Session'] = privilegedSession;
  }
  return config;
});

async function sessionCookieIsFresh() {
  try {
    await api.get('/auth/me', { skipAuthRefresh: true });
    return true;
  } catch (err) {
    if (err?.response?.status === 401) return false;
    throw err;
  }
}

const sessionRefresh = createSessionRefreshCoordinator({
  probe: sessionCookieIsFresh,
  refresh: () => api.post('/auth/refresh', null, { skipAuthRefresh: true }),
});

export function isDefinitiveSessionFailure(err) {
  return err?.response?.status === 401 || err?.response?.status === 403;
}

function redirectToLoginIfProtected() {
  const path = window.location.pathname;
  const hasToken = document.cookie.includes('tagdada_session=');
  // Creation flows that are previewable by logged-out visitors behind the
  // registration gate — don't bounce them to /login when there's no token.
  const isGated = !hasToken && (
    path === '/quick-invites' ||
    path.startsWith('/event-types/new') ||
    path === '/polls/new' ||
    path === '/queue/new'
  );
  if (shouldRedirectToLoginOn401(path, { isGated })) {
    window.location.href = '/login';
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status === 403 && PRIVILEGED_DENY_DETAILS.has(privilegedDenyDetail(err))) {
      clearPrivilegedSessionClient();
    }
    if (err?.response?.status !== 401 || typeof window === 'undefined') {
      return Promise.reject(err);
    }

    const original = err.config || {};
    // Coordinator requests must surface their result to the coordinator; they
    // must never recursively start refresh or redirect on their own.
    if (original.skipAuthRefresh) return Promise.reject(err);

    // One retry after a refresh attempt. A second 401 is a resource-level
    // rejection (wrong CalDAV password, failed identity proof, …) once the
    // session probe already succeeded — do not treat it as logout.
    if (original._sessionRefreshAttempted) {
      return Promise.reject(err);
    }
    original._sessionRefreshAttempted = true;

    try {
      await sessionRefresh.ensureFresh();
      return api(original);
    } catch (refreshErr) {
      // Network/429/5xx failures are transient. Preserve the current UI session
      // and let the caller fail normally; only an auth rejection ends it.
      if (isDefinitiveSessionFailure(refreshErr)) redirectToLoginIfProtected();
      return Promise.reject(refreshErr);
    }
  }
);
